import {
  Delivery,
  Warehouse,
  PickUp,
  StopType,
  UserError,
  Redelivery,
  Route,
  RoutingCohort,
  Stop,
} from '../graphql/types/delivery';
import config from '../config';
import { CombinedError } from 'urql';

export const CANNOT_CHANGE_DATA =
  'You cannot edit data in previous cut off windows.';
export const DISABLED_TOOLTIP_RUNNING =
  'You cannot edit details while the routing calculations are being performed.';
export const CANNOT_CHANGE_DEFINED_TRUCK_COUNT =
  'You cannot edit truck cut past cut off. To edit truck count past cut off please reach out to engineering.';

export const ON_CALL_URL =
  'https://forms.monday.com/forms/f4dd11b07f0200b827605e514c1db3a4?r=use1';

export const getTooltip = (
  routeImportRunning: boolean,
  isPastDate?: boolean,
  isTruckCountDefinedPresent?: boolean,
): string | undefined => {
  if (routeImportRunning) return DISABLED_TOOLTIP_RUNNING;
  if (isPastDate) return CANNOT_CHANGE_DATA;
  if (isTruckCountDefinedPresent) return CANNOT_CHANGE_DEFINED_TRUCK_COUNT;
  return undefined;
};

export const LOBBY_ROUTE_COLOR = '#000000';
export const INVALID_NIGHT_END = 'InvalidNightEnd';

// A never-optimized bucket route surfaced under its own Route Management tab:
// the third-party/Metrobi bucket or the customer-pickup bucket. These sites
// exclude the bucket but keep the Lobby, whose stops they count or scan.
export const isTabbedBucketRoute = (route: {
  thirdPartyRoute: boolean;
  customerPickupRoute: boolean;
}): boolean => route.thirdPartyRoute || route.customerPickupRoute;

// A system-created holding pen nobody drives: the Lobby, the customer-pickup
// bucket, or a provider's staging pen. Excluded from generic per-route
// surfaces (map squares, overweight, cohort). A route a GM marked for a
// provider is not synthetic: it is on the map and carries warnings. Sites
// that count only Odeko-driven routes also exclude isProviderRoute.
// bucketRoute is required so a document that does not select it fails tsc.
export const isSyntheticRoute = (route: {
  isLobby: boolean;
  customerPickupRoute: boolean;
  bucketRoute: boolean;
}): boolean => route.isLobby || route.customerPickupRoute || route.bucketRoute;

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Some GraphQL userError union members (e.g. AppointmentInPast, WithinLeadTime)
// carry no `message` field, so reading `errors[0].message` directly fails to
// type-check against the union. Widen the element type and fall back to a
// generic string when the first error has no message.
export const firstUserErrorMessage = (
  errors: ReadonlyArray<{ __typename?: string; message?: string }>,
): string => errors[0]?.message ?? 'Something went wrong. Please try again.';

// Nullable GraphQL scalars (e.g. an un-sequenced 3PD stop's `sequenceNumber`)
// arrive as `null`, which a default parameter does not catch — only `undefined`
// does. Coalesce both to '' so `.localeCompare` never receives null.
export const naturalSort = (a?: string | null, b?: string | null): number =>
  (a ?? '').localeCompare(
    b ?? '',
    navigator.languages[0] || navigator.language,
    {
      numeric: true,
      ignorePunctuation: true,
    },
  );

export const truncateString = (
  str: string,
  maxLength: number,
): { truncatedStr: string; truncated: boolean } => {
  const truncated = str?.length > maxLength;
  return {
    truncatedStr: truncated ? str.substr(0, maxLength - 1) + '...' : str,
    truncated,
  };
};

export const isDefined = (x: unknown): boolean => x !== undefined && x !== null;

export const addressString = (address: {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postalCode: string;
  state: string;
}): string => {
  const addLine2 = address?.addressLine2 ? ` ${address?.addressLine2}, ` : ', ';
  const addressStr = `${address?.addressLine1}${addLine2}${address?.city} ${address?.state} ${address?.postalCode}`;
  return addressStr;
};

export const getSelectedWarehouse = (
  id?: string,
  warehouses?: Warehouse[],
): Warehouse | undefined => {
  if (id && warehouses) {
    return warehouses.find((warehouse) => warehouse.id === id);
  }
  return undefined;
};

export const findStopName = (
  stop: Delivery | PickUp | Redelivery,
  selectedWarehouseId?: string,
): string => {
  const typename = stop.__typename;
  let stopName;
  switch (typename) {
    case StopType.Delivery:
    case StopType.Redelivery:
      stopName = stop.location?.businessName ?? 'Unknown Location';
      break;
    case StopType.PickUp:
      stopName = stop.vendor.legalName ?? 'Unknown Vendor';
      break;
    default:
      stopName = 'Unknown Stop';
      break;
  }
  // Add state code before stop name. Just for EWR1.
  let state = '';
  if (selectedWarehouseId === getEWR1Id()) {
    const stopState =
      stop.__typename === 'PickUp'
        ? stop.vendor.address?.state
        : stop.location?.address.state;
    if (stopState === 'NY' || stopState === 'New York') state = 'NY: ';
    if (stopState === 'NJ' || stopState === 'New Jersey') state = 'NJ: ';
  }

  return `${state}${stopName}`;
};

export type Coords = { lat: number; lng: number };

export const getStopCoords = (
  stop?: Delivery | PickUp | Redelivery,
): Coords | undefined => {
  const address =
    stop?.__typename === 'PickUp'
      ? stop?.vendor.address
      : stop?.location?.address;

  if (!address?.latitude || !address?.longitude) {
    return undefined;
  }

  return {
    lat: address.latitude,
    lng: address.longitude,
  };
};

export const isUserErrorInArray = (
  errorTypeName: string,
  userErrors?: any,
): boolean =>
  userErrors?.find(
    (userError: { __typename: string }) =>
      userError.__typename === errorTypeName,
  ) !== undefined;

export const getEWR1Id = (): string => {
  const env = config().environment;
  switch (env) {
    case 'development':
    case 'stage-2':
      return 'c57f4a46-fdfc-48ff-9a89-f98c06a65955';
    case 'stage-1':
    case 'stage':
      return '7215746f-04bb-4616-b586-e28e62cc73bb';
    case 'prod':
    case 'prod-1':
    default:
      return 'a7e01b5e-6576-49d5-a9d0-b73bf5be2768';
  }
};

export const getErrorCode = ({
  error,
  expectedData,
  userErrors,
}: {
  error?: CombinedError;
  expectedData?: any;
  userErrors?: UserError[];
}): string => {
  if (error) {
    return 'GraphQLError';
  }
  if (userErrors) {
    let combinedErrors = '';
    userErrors.forEach((userError) => {
      combinedErrors += `${userError.__typename} `;
    });
    return combinedErrors;
  }
  if (!expectedData) {
    return 'NoDataReturned';
  }
  return 'UnknownError';
};

export const updateSequenceNumbersRedelivery = (route: Route): Route => {
  const updatedRedeliveryStops = route.redeliveryStops.map((redeliveryStop) => {
    const location = redeliveryStop.location
      ? {
          ...redeliveryStop.location,
          businessName: `Re: ${redeliveryStop.location.businessName}`,
        }
      : undefined;
    return {
      ...redeliveryStop,
      sequenceNumber: (
        Number(redeliveryStop.sequenceNumber ?? '0') +
        (route.stops?.length ?? 0)
      ).toString(),
      location,
    };
  });
  return {
    ...route,
    redeliveryStops: updatedRedeliveryStops,
  };
};

export const decodeSearchParams = (searchParams: any): any => {
  const entries = new Map(searchParams.entries());
  return Object.fromEntries(entries);
};

export const getStopTotals = (
  routes: Route[],
): {
  totalPickUpStops: number;
  totalDeliveryStops: number;
  totalRedeliveryStops: number;
  totalStops: number;
} => {
  let totalPickUpStops = 0;
  let totalDeliveryStops = 0;
  let totalRedeliveryStops = 0;
  // Exclude the tabbed bucket routes: their stops are surfaced separately under
  // the Metrobi and Customer Pick Ups tabs and would otherwise be
  // double-counted here too. The Lobby stays in, as before.
  routes
    .filter((route) => !isTabbedBucketRoute(route))
    .forEach((route) => {
      const deliveryStops = (route.stops ?? []).filter(
        (stop) => stop.__typename === 'Delivery',
      );
      const pickUpStops = (route.stops ?? []).filter(
        (stop) => stop.__typename === 'PickUp',
      );
      const redeliveryStops = route.redeliveryStops ?? [];
      totalDeliveryStops = (deliveryStops.length ?? 0) + totalDeliveryStops;
      totalPickUpStops = (pickUpStops.length ?? 0) + totalPickUpStops;
      totalRedeliveryStops =
        (redeliveryStops.length ?? 0) + totalRedeliveryStops;
    });
  return {
    totalDeliveryStops,
    totalPickUpStops,
    totalRedeliveryStops,
    totalStops: totalPickUpStops + totalDeliveryStops + totalRedeliveryStops,
  };
};

export const getRoutingCohorts = (
  routes: Route[],
): (RoutingCohort & { stops: Stop[] })[] => {
  const cohorts: (RoutingCohort & { stops: Stop[] })[] = [];
  routes.forEach((route) => {
    route.stops?.forEach((stop) => {
      if (stop.__typename !== 'Delivery') return;
      if (!stop.location?.routingCohort) return;
      const index = cohorts.findIndex(
        (cohort) => cohort.id === stop.location?.routingCohort?.id,
      );
      if (index === -1)
        cohorts.push({ ...stop.location?.routingCohort, stops: [stop] });
      else cohorts[index].stops.push(stop);
    });
  });
  return cohorts;
};

export const getPercent = (numerator: number, denominator: number): number =>
  Math.ceil((numerator / denominator) * 100);

export const compareValues = (
  first: any,
  second: any,
  direction: 'asc' | 'desc',
): number => {
  // Handle null/undefined values
  if (first === null && second === null) return 0;
  if (first === null) return direction === 'asc' ? -1 : 1;
  if (second === null) return direction === 'asc' ? 1 : -1;

  // Handle different types
  if (typeof first === 'string' && typeof second === 'string') {
    return compareStrings(first, second, direction);
  }

  if (typeof first === 'number' && typeof second === 'number') {
    return compareNumbers(first, second, direction);
  }

  // Fallback for other types - convert to string
  return compareStrings(String(first), String(second), direction);
};

const compareStrings = (
  first: string,
  second: string,
  direction: 'asc' | 'desc',
): number =>
  direction === 'asc'
    ? first.localeCompare(second)
    : second.localeCompare(first);

const compareNumbers = (
  first: number,
  second: number,
  direction: 'asc' | 'desc',
): number => (direction === 'asc' ? first - second : second - first);

/**
 * Sums an array of nullable values. Returns null if all values are null.
 *
 * @param values - Array of values that may contain nulls
 * @returns The sum of all non-null values, or null if all values are null
 */
export const sumNullableValues = (
  values: Array<number | null>,
): number | null => {
  // Check if at least one value is not null
  if (values.some((value) => value !== null)) {
    // Sum all non-null values
    return values.reduce((sum: number, value) => sum + (value ?? 0), 0);
  }
  return null;
};
